/**
 * Rebuild Parent IDs Utility
 *
 * Fixes component hierarchy by rebuilding parentId relationships from children arrays.
 * This is needed when components are loaded from AI generation or templates that don't
 * properly set parentId fields.
 *
 * Week 3: Selection System - Data integrity fix
 */

import type { Component } from '@/types';
import { devLog, devWarn } from './devLog';

/**
 * Rebuild parentId fields for all components based on children arrays
 *
 * Scans all flex-container components and sets parentId on their children.
 * This ensures the component hierarchy is properly maintained.
 *
 * @param components - Array of all components in the editor
 * @returns Updated components array with corrected parentId values
 *
 * @example
 * ```typescript
 * const components = editorStore.components;
 * const fixed = rebuildParentIds(components);
 * editorStore.setComponents(fixed);
 * ```
 */
export function rebuildParentIds(components: Component[]): Component[] {
  devLog('LAYOUT', '🔧 Starting parentId rebuild', {
    totalComponents: components.length,
  });

  // Create a map for fast component lookup
  const componentMap = new Map<string, Component>();
  components.forEach((c) => {
    componentMap.set(c.id, c);
  });

  // Track changes for logging
  let fixedCount = 0;
  let alreadyCorrect = 0;

  // First pass: Clear all parentIds (we'll rebuild from scratch)
  const clearedComponents = components.map((component) => {
    return { ...component, parentId: undefined as string | undefined };
  });

  // Second pass: Set parentId based on children arrays
  clearedComponents.forEach((component) => {
    // Check if this component has children (flex-container, etc.)
    const children = (component as any).children;

    if (children && Array.isArray(children) && children.length > 0) {
      devLog('LAYOUT', `📦 Processing container: ${component.id}`, {
        containerId: component.id,
        containerType: component.type,
        childCount: children.length,
        childIds: children,
      });

      // Set parentId on each child
      children.forEach((childId: string) => {
        const childIndex = clearedComponents.findIndex((c) => c.id === childId);

        if (childIndex !== -1) {
          const child = clearedComponents[childIndex];

          // Check if parentId needs updating
          if (child.parentId !== component.id) {
            devLog('LAYOUT', `  ✅ Setting parentId for ${childId}`, {
              childId,
              oldParentId: child.parentId || 'none',
              newParentId: component.id,
            });

            clearedComponents[childIndex] = {
              ...child,
              parentId: component.id,
            };

            fixedCount++;
          } else {
            alreadyCorrect++;
          }
        } else {
          devWarn('LAYOUT', `  ⚠️ Child component not found: ${childId}`, {
            parentId: component.id,
            missingChildId: childId,
          });
        }
      });
    }
  });

  devLog('LAYOUT', '✨ ParentId rebuild complete', {
    totalComponents: components.length,
    fixedCount,
    alreadyCorrect,
    summary: `Fixed ${fixedCount} components, ${alreadyCorrect} already correct`,
  });

  return clearedComponents;
}

/**
 * Validate component hierarchy integrity
 *
 * Checks for common hierarchy issues like circular references,
 * orphaned children, and mismatched parent-child relationships.
 *
 * @param components - Array of components to validate
 * @returns Validation result with any issues found
 */
export function validateHierarchy(components: Component[]): {
  valid: boolean;
  issues: string[];
  warnings: string[];
} {
  const issues: string[] = [];
  const warnings: string[] = [];

  const componentMap = new Map(components.map((c) => [c.id, c]));

  components.forEach((component) => {
    // Check if parentId references a valid component
    if (component.parentId) {
      const parent = componentMap.get(component.parentId);

      if (!parent) {
        issues.push(
          `Component "${component.id}" has parentId "${component.parentId}" but parent doesn't exist`
        );
      } else {
        // Check if parent's children array includes this component
        const parentChildren = (parent as any).children;
        if (parentChildren && Array.isArray(parentChildren)) {
          if (!parentChildren.includes(component.id)) {
            warnings.push(
              `Component "${component.id}" has parentId "${component.parentId}" but parent's children array doesn't include it`
            );
          }
        }
      }
    }

    // Check if children array references valid components
    const children = (component as any).children;
    if (children && Array.isArray(children)) {
      children.forEach((childId: string) => {
        const child = componentMap.get(childId);

        if (!child) {
          issues.push(
            `Component "${component.id}" has child "${childId}" in children array but child doesn't exist`
          );
        } else if (child.parentId !== component.id) {
          warnings.push(
            `Component "${component.id}" has child "${childId}" in children array but child's parentId is "${child.parentId || 'undefined'}"`
          );
        }
      });
    }
  });

  const valid = issues.length === 0;

  if (!valid || warnings.length > 0) {
    devLog('LAYOUT', '🔍 Hierarchy validation results', {
      valid,
      issueCount: issues.length,
      warningCount: warnings.length,
      issues,
      warnings,
    });
  }

  return { valid, issues, warnings };
}
